<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import Dialog from 'primevue/dialog';
import Popover from 'primevue/popover';
import Button from 'primevue/button';
import Chip from 'primevue/chip';
import ProgressSpinner from 'primevue/progressspinner';
import UserAvatar from '@/components/UserAvatar.vue';
import DrawingCanvas from '@/components/DrawingCanvas.vue';
import type { WallPost } from '@/types/api';
import { CHAT_EMOJIS, REACTION_EMOJIS } from '@/types/api';
import { createWallPost, fetchWallPosts, reactToPost } from '@/lib/apiClient';
import { attachWallAuthor, normalizeWallPostsResponse } from '@/lib/wallPosts';
import { compressDataUrl } from '@/lib/compressImage';
import { useAuth } from '@/composables/useAuth';

const toast = useToast();
const { token, user } = useAuth();

const loading = ref(true);
const submitting = ref(false);
const compressingDrawing = ref(false);
const expanded = ref(false);
const drawingVisible = ref(false);
const posts = ref<WallPost[]>([]);
const message = ref('');
const drawing = ref<string | null>(null);
const messagesRef = ref<HTMLElement | null>(null);
const emojiPopover = ref();

async function loadPosts() {
  if (!token.value) return;

  try {
    const response = await fetchWallPosts(token.value);
    posts.value = normalizeWallPostsResponse(response);
    await nextTick();
    scrollToBottom();
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Не удалось загрузить стену',
      detail: error instanceof Error ? error.message : 'Ошибка сервера',
      life: 4000,
    });
  }
}

function scrollToBottom() {
  const el = messagesRef.value;
  if (el) el.scrollTop = el.scrollHeight;
}

onMounted(async () => {
  try {
    await loadPosts();
  } finally {
    loading.value = false;
  }
});

watch(
  () => posts.value.length,
  () => nextTick(scrollToBottom),
);

function toggleExpanded() {
  expanded.value = !expanded.value;
}

function openDrawing() {
  drawingVisible.value = true;
}

function applyDrawing() {
  drawingVisible.value = false;
}

function clearDrawing() {
  drawing.value = null;
}

function insertEmoji(emoji: string, event?: Event) {
  message.value += emoji;
  emojiPopover.value?.hide(event);
}

async function submitPost() {
  if (!token.value) return;
  if (!message.value.trim() && !drawing.value) return;

  submitting.value = true;
  compressingDrawing.value = Boolean(drawing.value);

  try {
    const drawingPayload = drawing.value ? await compressDataUrl(drawing.value) : null;
    compressingDrawing.value = false;
    const created = await createWallPost(token.value, {
      message: message.value.trim(),
      drawing: drawingPayload,
    });
    message.value = '';
    drawing.value = null;
    posts.value.push(attachWallAuthor(created.post, created.author));
    await nextTick();
    scrollToBottom();
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error instanceof Error ? error.message : 'Не удалось отправить',
      life: 3000,
    });
  } finally {
    submitting.value = false;
    compressingDrawing.value = false;
  }
}

function onEnter(event: KeyboardEvent) {
  if (!event.shiftKey) {
    event.preventDefault();
    submitPost();
  }
}

async function addReaction(postId: number, emoji: string) {
  if (!token.value) return;

  try {
    const { reactions } = await reactToPost(token.value, postId, emoji);
    const index = posts.value.findIndex((post) => post.id === postId);
    if (index >= 0) {
      posts.value[index] = { ...posts.value[index], reactions };
    }
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Не удалось поставить реакцию',
      detail: error instanceof Error ? error.message : 'Ошибка сервера',
      life: 3000,
    });
  }
}
</script>

<template>
  <div class="wall-page" :class="{ 'wall-page--expanded': expanded }">
    <header class="wall-page__header">
      <div>
        <h1>Стена Плача</h1>
        <p>Сюда стекается всё, что нельзя промолчать. Или можно — но скучно.</p>
      </div>
      <Button
        :icon="expanded ? 'pi pi-window-minimize' : 'pi pi-window-maximize'"
        severity="secondary"
        text
        rounded
        :aria-label="expanded ? 'Свернуть' : 'Развернуть'"
        @click="toggleExpanded"
      />
    </header>

    <section class="chat" :class="{ 'chat--expanded': expanded }">
      <div ref="messagesRef" class="chat__messages">
        <ProgressSpinner v-if="loading" class="chat__spinner" />

        <div v-else-if="posts.length === 0" class="chat__empty">
          <span>💬</span>
          <p>Пока нет сообщений. Напиши первым!</p>
        </div>

        <div
          v-for="post in posts"
          :key="post.id"
          class="chat__bubble-row"
          :class="{ 'chat__bubble-row--mine': post.userId === user?.id }"
        >
          <UserAvatar
            v-if="post.userId !== user?.id"
            :nickname="post.nickname"
            :avatar="post.avatar"
            size="normal"
          />
          <div
            class="chat__bubble"
            :class="{ 'chat__bubble--mine': post.userId === user?.id }"
          >
            <div v-if="post.userId !== user?.id" class="chat__author">{{ post.nickname }}</div>
            <p v-if="post.message" class="chat__text">{{ post.message }}</p>
            <img v-if="post.drawing" :src="post.drawing" alt="Рисунок" class="chat__drawing" loading="lazy" decoding="async" />
            <div class="chat__meta">
              <small>{{ new Date(post.createdAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) }}</small>
              <div class="chat__reactions">
                <button
                  v-for="emoji in REACTION_EMOJIS"
                  :key="`${post.id}-${emoji}`"
                  type="button"
                  class="chat__react-btn"
                  @click="addReaction(post.id, emoji)"
                >
                  {{ emoji }}
                </button>
                <Chip
                  v-for="reaction in post.reactions ?? []"
                  :key="`${post.id}-${reaction.emoji}-count`"
                  :label="`${reaction.emoji} ${reaction.count}`"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="drawing" class="chat__attachment">
        <img :src="drawing" alt="Прикреплённый рисунок" />
        <Button icon="pi pi-times" severity="danger" text rounded @click="clearDrawing" />
      </div>

      <footer class="chat__composer">
        <Button
          icon="pi pi-face-smile"
          severity="secondary"
          text
          rounded
          aria-label="Эмодзи"
          @click="emojiPopover.toggle($event)"
        />
        <Popover ref="emojiPopover">
          <div class="emoji-grid">
            <button
              v-for="emoji in CHAT_EMOJIS"
              :key="emoji"
              type="button"
              class="emoji-grid__btn"
              @click="insertEmoji(emoji, $event)"
            >
              {{ emoji }}
            </button>
          </div>
        </Popover>

        <Button
          icon="pi pi-pencil"
          severity="secondary"
          text
          rounded
          aria-label="Добавить рисунок"
          @click="openDrawing"
        />

        <textarea
          v-model="message"
          class="chat__input"
          rows="1"
          placeholder="Сообщение..."
          @keydown.enter="onEnter"
        />

        <Button
          icon="pi pi-send"
          class="chat__send"
          :loading="submitting"
          :aria-label="compressingDrawing ? 'Сжимаем рисунок…' : 'Отправить'"
          @click="submitPost"
        />
      </footer>
    </section>

    <Dialog
      v-model:visible="drawingVisible"
      modal
      header="Нарисовать"
      :style="{ width: 'min(560px, 94vw)' }"
      :draggable="false"
    >
      <DrawingCanvas v-model="drawing" />
      <template #footer>
        <Button label="Отмена" severity="secondary" text @click="drawingVisible = false" />
        <Button label="Прикрепить" icon="pi pi-check" @click="applyDrawing" />
      </template>
    </Dialog>
  </div>
</template>

<style scoped>
.wall-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  gap: 0.75rem;
}

.wall-page--expanded {
  position: fixed;
  inset: 0;
  z-index: 50;
  padding: 1rem;
  background: rgba(255, 247, 251, 0.98);
}

.wall-page__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-shrink: 0;
}

.wall-page__header h1 {
  margin: 0 0 0.2rem;
  color: #831843;
}

.wall-page__header p {
  margin: 0;
  color: #9d174d;
  font-size: 0.92rem;
}

.chat {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #fce7f3;
  border-radius: 1.25rem;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(219, 39, 119, 0.08);
  max-height: 100%;
}

.wall-page .chat {
  resize: none;
  min-height: 0;
}

.chat--expanded {
  resize: none;
  min-height: 0;
  max-height: none;
}

.chat__messages {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background: linear-gradient(180deg, #fff7fb 0%, #ffffff 100%);
}

.chat__spinner {
  margin: auto;
}

.chat__empty {
  margin: auto;
  text-align: center;
  color: #be185d;
}

.chat__empty span {
  font-size: 2rem;
  display: block;
  margin-bottom: 0.5rem;
}

.chat__bubble-row {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  max-width: 85%;
}

.chat__bubble-row--mine {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.chat__bubble {
  background: #fff;
  border: 1px solid #fce7f3;
  border-radius: 1rem 1rem 1rem 0.35rem;
  padding: 0.65rem 0.85rem;
  box-shadow: 0 4px 14px rgba(219, 39, 119, 0.06);
}

.chat__bubble--mine {
  background: linear-gradient(135deg, #fb7185, #f472b6);
  border-color: transparent;
  color: white;
  border-radius: 1rem 1rem 0.35rem 1rem;
}

.chat__author {
  font-size: 0.78rem;
  font-weight: 700;
  color: #db2777;
  margin-bottom: 0.25rem;
}

.chat__text {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.chat__bubble--mine .chat__text {
  color: white;
}

.chat__drawing {
  display: block;
  max-width: min(280px, 100%);
  border-radius: 0.75rem;
  margin-top: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.35);
}

.chat__meta {
  margin-top: 0.35rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.chat__meta small {
  opacity: 0.75;
  font-size: 0.72rem;
}

.chat__reactions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  align-items: center;
}

.chat__react-btn {
  border: none;
  background: rgba(255, 255, 255, 0.65);
  border-radius: 999px;
  padding: 0.1rem 0.35rem;
  cursor: pointer;
  font-size: 0.85rem;
}

.chat__attachment {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-top: 1px solid #fce7f3;
  background: #fff7fb;
}

.chat__attachment img {
  height: 56px;
  border-radius: 0.5rem;
  border: 1px solid #fbcfe8;
}

.chat__composer {
  display: flex;
  align-items: flex-end;
  gap: 0.35rem;
  padding: 0.65rem 0.75rem;
  padding-bottom: max(0.65rem, env(safe-area-inset-bottom, 0));
  border-top: 1px solid #fce7f3;
  background: #fff;
  flex-shrink: 0;
}

@media (max-width: 640px) {
  .wall-page__header h1 {
    font-size: 1.15rem;
  }

  .wall-page__header p {
    font-size: 0.82rem;
  }

  .chat__bubble-row {
    max-width: 92%;
  }

  .chat__input {
    font-size: 16px;
  }

  .chat__composer :deep(.p-button) {
    min-width: 44px;
    min-height: 44px;
    touch-action: manipulation;
  }
}

.chat__input {
  flex: 1;
  resize: none;
  border: 1px solid #fbcfe8;
  border-radius: 1.25rem;
  padding: 0.65rem 1rem;
  font: inherit;
  min-height: 42px;
  max-height: 120px;
  outline: none;
  background: #fff7fb;
}

.chat__input:focus {
  border-color: #f472b6;
}

.chat__send :deep(.p-button) {
  background: linear-gradient(135deg, #fb7185, #f472b6);
  border: none;
}

.emoji-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 0.25rem;
  max-width: 240px;
}

.emoji-grid__btn {
  border: none;
  background: transparent;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.35rem;
  border-radius: 0.5rem;
}

.emoji-grid__btn:hover {
  background: #fdf2f8;
}
</style>
